import React, { useState, useEffect, useContext, useMemo } from "react";
import {
    Stack,
    TextField,
    TooltipHost,
    Checkbox,
    PrimaryButton,
    ITextFieldStyles,
    IStackStyles,
    ICheckboxStyles,
    ISliderStyles,
    DirectionalHint,
    IconButton,
    Slider,
    IDropdownOption,
    Dropdown,
    IDropdownStyles,
    ITextFieldProps,
    IRenderFunction,
    Toggle,
    Spinner,
    SpinnerSize
} from "@fluentui/react";
import { toast } from "react-toastify";
import styles from "./CompEditForm.module.css";
import TagInput from "../TagInput/TagInput";
import { gactivecomp, gselectedgroup, gLicense, licmeasure, licsvclevel, lictype } from "../../interfaces";
import { GlobalContext } from "../../GlobalContext"; // Tests¦Context Accross Routes (ex. Chat/Search)
import { getHeaderGlob } from "../../util_glob";
import { dropdownStylesCompAdmin, tooltipStylesCompAdmin, stackStylesCompAdmin, toggleStylesCompEdit } from "../../styles_glob";

// Countdown Timer Component for pending license activation
const CountdownTimer: React.FC<{ targetDate: Date }> = ({ targetDate }) => {
    const [timeLeft, setTimeLeft] = useState(() => {
        const diff = targetDate.getTime() - Date.now();
        return diff > 0 ? diff : 0;
    });

    useEffect(() => {
        if (timeLeft <= 0) return;
        const timer = setInterval(() => {
            const diff = targetDate.getTime() - Date.now();
            setTimeLeft(diff > 0 ? diff : 0);
        }, 1000);
        return () => clearInterval(timer);
    }, [targetDate]);

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    return (
        <div className={styles.countdownContainer}>
            <div className={styles.countdownHeader}>
                <span className={styles.countdownIcon}>🚀</span>
                <span className={styles.countdownTitle}>Activating In</span>
            </div>
            <div className={styles.countdownTimer}>
                <div className={styles.countdownUnit}>
                    <span className={styles.countdownValue}>{String(days).padStart(2, "0")}</span>
                    <span className={styles.countdownLabel}>Days</span>
                </div>
                <span className={styles.countdownSeparator}>:</span>
                <div className={styles.countdownUnit}>
                    <span className={styles.countdownValue}>{String(hours).padStart(2, "0")}</span>
                    <span className={styles.countdownLabel}>Hours</span>
                </div>
                <span className={styles.countdownSeparator}>:</span>
                <div className={styles.countdownUnit}>
                    <span className={styles.countdownValue}>{String(minutes).padStart(2, "0")}</span>
                    <span className={styles.countdownLabel}>Mins</span>
                </div>
                <span className={styles.countdownSeparator}>:</span>
                <div className={styles.countdownUnit}>
                    <span className={`${styles.countdownValue} ${styles.countdownSeconds}`}>{String(seconds).padStart(2, "0")}</span>
                    <span className={styles.countdownLabel}>Secs</span>
                </div>
            </div>
        </div>
    );
};
import { compTierOptions, remapLicanceCodeToName } from "../../lsts";
import { qIsG } from "../../api/qpi";
import { qEditComp, getLicState, getLicStateUsage } from "../../api";

interface ICompEditFormProps {
    selectedGroup: gselectedgroup;
    activeComp: gactivecomp;
}

const CompEditForm: React.FC<ICompEditFormProps> = ({ selectedGroup, activeComp }) => {
    const { loggedInUser } = useContext(GlobalContext);
    const [editedComp, setEditedComp] = useState<gactivecomp>({ ...activeComp });
    const [isLoadingAPI, setIsLoadingAPI] = useState(false);
    const [allowedDomains, setAllowedDomains] = useState(editedComp.alloweddomains != "" ? editedComp.alloweddomains?.split(";") : []);
    const [isG, setIsG] = useState<boolean>(false);
    const [licensesArr, setLicensesArr] = useState<gLicense[]>([]);
    const [loadingLicenses, setLoadingLicenses] = useState(false);

    useEffect(() => {
        setEditedComp({ ...activeComp });
        qIsG(loggedInUser).then(res => setIsG(res));
    }, [activeComp]);

    useEffect(() => {
        if (activeComp.companyid && activeComp.permissionlevel > 2) {
            setLoadingLicenses(true);
            const headers = getHeaderGlob(loggedInUser);
            Promise.all([
                getLicState(activeComp.companyid, headers),
                getLicStateUsage(activeComp.companyid, headers)
            ])
                .then(([allLics, usageLics]) => {
                    // usage rows from bucketed endpoint; everything else from legacy endpoint
                    const nonUsage = (allLics || []).filter(l => l.lic_type !== "usage");
                    setLicensesArr([...(usageLics || []), ...nonUsage]);
                })
                .finally(() => setLoadingLicenses(false));
        }
    }, [activeComp.companyid]);

    useEffect(() => {
        if (isLoadingAPI != false) setIsLoadingAPI(false);
    }, []);

    const handleChange = (field: keyof gactivecomp, value: string | number | boolean) => {
        setEditedComp(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        setIsLoadingAPI(true);
        qEditComp(activeComp, editedComp, getHeaderGlob(loggedInUser)).then(resp => {
            if (!resp.ok) {
                toast.error("Error Saving Company Config");
            } else {
                toast.success("Company Config Saved");
            }
            setIsLoadingAPI(false);
        })
    };

    const onAllowedDomainsChange = (tags: string[]) => {
        setAllowedDomains(tags);
        if (tags) {
            setEditedComp(prev => ({ ...prev, alloweddomains: tags?.join(";").toLowerCase() }));
        } else {
            setEditedComp(prev => ({ ...prev, alloweddomains: "" }));
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return "—";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    };

    const getRemainingTerm = (activatedOn: string, termMonths: number) => {
        if (!activatedOn || !termMonths) return { days: 0, expired: true, warning: false, critical: false, pending: false, pendingDays: 0 };
        const activated = new Date(activatedOn);
        const now = new Date();

        // check if activation is in the future
        const pendingMs = activated.getTime() - now.getTime();
        if (pendingMs > 0) {
            const pendingDays = Math.ceil(pendingMs / (1000 * 60 * 60 * 24));
            return { days: termMonths * 30, expired: false, warning: false, critical: false, pending: true, pendingDays };
        }

        const expiresAt = new Date(activated);
        expiresAt.setMonth(expiresAt.getMonth() + termMonths);
        const diffMs = expiresAt.getTime() - now.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return {
            days: diffDays,
            expired: diffDays <= 0,
            critical: diffDays > 0 && diffDays <= 7,
            warning: diffDays > 7 && diffDays <= 30,
            pending: false,
            pendingDays: 0
        };
    };

    const getServiceLevelBadge = (level: string) => {
        const colors: Record<string, string> = {
            premium: "#00c23d",
            usage: "#48A111",
            enterprise: "#f59e0b",
            "usage interval": "#C13383"
        };
        return colors[level?.toLowerCase()] || "#6b7280";
    };

    const getUsageStatus = (used: number, total: number) => {
        if (total <= 0) return { percent: 0, status: "normal", color: "#10b981" };
        const percent = Math.min((used / total) * 100, 100);
        const overPercent = used > total ? ((used - total) / total) * 100 : 0;

        if (used > total) {
            return { percent: 100, overPercent, status: "over", color: "#dc2626" };
        } else if (percent >= 90) {
            return { percent, overPercent: 0, status: "critical", color: "#ef4444" };
        } else if (percent >= 75) {
            return { percent, overPercent: 0, status: "warning", color: "#f59e0b" };
        }
        return { percent, overPercent: 0, status: "normal", color: "#10b981" };
    };

    const getTermStatus = (activatedOn: string, termMonths: number) => {
        if (!activatedOn || !termMonths) return { percentRemaining: 0, totalDays: 0, remainingDays: 0, status: "expired" };
        const activated = new Date(activatedOn);
        const expiresAt = new Date(activated);
        expiresAt.setMonth(expiresAt.getMonth() + termMonths);
        const now = new Date();

        const totalMs = expiresAt.getTime() - activated.getTime();
        const remainingMs = expiresAt.getTime() - now.getTime();
        const totalDays = Math.ceil(totalMs / (1000 * 60 * 60 * 24));
        const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));

        if (remainingDays <= 0) return { percentRemaining: 0, totalDays, remainingDays: 0, status: "expired" };

        const percentRemaining = Math.min((remainingDays / totalDays) * 100, 100);

        if (percentRemaining <= 10 || remainingDays <= 7) {
            return { percentRemaining, totalDays, remainingDays, status: "critical" };
        } else if (percentRemaining <= 25 || remainingDays <= 30) {
            return { percentRemaining, totalDays, remainingDays, status: "warning" };
        }
        return { percentRemaining, totalDays, remainingDays, status: "normal" };
    };

    return (
        <div className={styles.editModalWrap}>
            <Stack styles={stackStylesCompAdmin}>
                <div className={styles.editFormSectionTitle}>
                    <span>General Configuration</span>
                </div>

                <TooltipHost content="Licensing Tier" directionalHint={DirectionalHint.topAutoEdge} calloutProps={tooltipStylesCompAdmin}>
                    <Dropdown
                        label="Licensing Tier"
                        options={compTierOptions}
                        selectedKey={editedComp.licensingkind.toString()}
                        onChange={(_, option) => handleChange("licensingkind", Number(option?.key))}
                        disabled={!isG}
                        styles={dropdownStylesCompAdmin}
                    />
                </TooltipHost>

                {/* license cards section */}
                {activeComp.permissionlevel > 2 && (() => {
                    const usageLics = licensesArr.filter(l => l.lic_type === ("usage" as lictype));

                    // Helper ￫ Build a map of the latest activated_on timestamp for each op_type among usage licenses
                    const latestUsageByOp = usageLics.reduce<Record<string, number>>((acc, l) => {
                        const key = l.op_type || "";
                        const t = new Date(l.activated_on || 0).getTime();
                        if (t > (acc[key] || 0)) acc[key] = t;
                        return acc;
                    }, {});

                    // Helper ￫ Determine if Past Usage. Past Usage: a usage license that has been superseded by a later same-op_type usage license (i.e. predecessor fully spent).
                    const isPastUsage = (l: gLicense) => {
                        if (l.lic_type !== ("usage" as lictype)) return false;
                        const latestForOp = latestUsageByOp[l.op_type || ""] || 0;
                        const hasLater    = new Date(l.activated_on || 0).getTime() < latestForOp;
                        if (!hasLater) return false;   // not superseded within its op_type
                        const expired       = getRemainingTerm(l.activated_on, l.term).expired;
                        const inactive      = l.is_active != 1;
                        const cap           = l.usage_amount ?? 0;
                        const used          = l.allocated_pages ?? l.total_pages_synced ?? 0;
                        const fullyConsumed = cap > 0 && used >= cap;
                        return inactive || expired || fullyConsumed;
                    };

                    // Helper ￫ Determine if Standby. Standby: an active usage license whose earlier same-op_type sibling still has consumption capacity left (i.e. predecessor not yet fully spent).
                    const isStandbyUsage = (l: gLicense) => {
                        if (l.lic_type !== ("usage" as lictype)) return false;
                        const myTime = new Date(l.activated_on || 0).getTime();
                        return usageLics.some(other => {
                            if (other === l) return false;
                            if ((other.op_type || "") !== (l.op_type || "")) return false;
                            const otherTime = new Date(other.activated_on || 0).getTime();
                            if (otherTime >= myTime) return false;   // must be earlier
                            if (other.is_active != 1) return false;
                            if (getRemainingTerm(other.activated_on, other.term).expired) return false;
                            const cap  = other.usage_amount ?? 0;
                            const used = other.allocated_pages ?? other.total_pages_synced ?? 0;
                            return cap <= 0 || used < cap;   // predecessor has room left
                        });
                    };

                    return (
                    <div className={styles.licensesSection}>
                        <div className={styles.licensesSectionHeader}>
                            <span className={styles.licensesSectionTitle}>Active Licenses</span>
                            {loadingLicenses && <Spinner size={SpinnerSize.small} />}
                        </div>
                        {!loadingLicenses && licensesArr.filter(l => l.is_active == 1 && !getRemainingTerm(l.activated_on, l.term).expired && !isPastUsage(l)).length === 0 && (
                            <div className={styles.noLicenses}>No Active Licenses</div>
                        )}
                        {/* active licenses */}
                        <div className={styles.licensesGrid}>
                            {/* filter & display only active licenses that are not expired and not past usage */ }
                            {licensesArr.filter(l => l.is_active == 1 && !getRemainingTerm(l.activated_on, l.term).expired && !isPastUsage(l)).map(lic => {
                                const remaining = getRemainingTerm(lic.activated_on, lic.term);
                                const cardClass = remaining.pending
                                    ? styles.licenseCardPending
                                    : remaining.critical
                                        ? styles.licenseCardCritical
                                        : remaining.warning
                                            ? styles.licenseCardWarning
                                            : styles.licenseCardActive;

                                const opTypeLabelClass = remaining.pending
                                    ? styles.licenseOpTypeLabelPending
                                    : remaining.critical
                                        ? styles.licenseOpTypeLabelCritical
                                        : remaining.warning
                                            ? styles.licenseOpTypeLabelWarning
                                            : styles.licenseOpTypeLabelActive;

                                return (
                                    <div key={lic.id} className={`${styles.licenseCard} ${cardClass}`}>
                                        {isStandbyUsage(lic) && (
                                            <span className={styles.standbyRibbon} title="This license is active and ready to take over once the earlier license is fully consumed.">
                                                Standby
                                            </span>
                                        )}
                                        <div className={`${styles.licenseOpTypeLabel} ${opTypeLabelClass}`}>
                                            <div className={styles.opTypeLabelText}>
                                                {remapLicanceCodeToName[lic.op_type] || ""}
                                            </div>
                                        </div>



                                        <div className={styles.licenseCardHeader}>
                                            <span className={styles.licenseType}>{lic.lic_type || "License"}</span>
                                            <span className={styles.serviceLevelBadge} style={{ backgroundColor: getServiceLevelBadge(lic.service_level) }}>
                                                {lic.service_level || "Standard"}
                                            </span>
                                        </div>
                                        {(lic.lic_extra_props?.['Interval Pricing'] != undefined && lic.lic_extra_props?.['Interval Pricing'] > 0) ? (
                                            (() => {
                                                const rules = lic.lic_extra_props?.['Interval Pricing Rules'];
                                                const minUsage = rules?.['Minimum Monthly Usage'] ?? 0;
                                                const maxUsage = rules?.['Maximum Monthly Usage'] ?? 0;
                                                const supportUnder = rules?.['Support Price Under Maximum'] ?? 0;
                                                const supportOver = rules?.['Support Price Over Maximum'] ?? 0;
                                                const officialActivateDate = lic.lic_extra_props?.['Official Activate Date'] ?? "";
                                                // official activation month in YYYY-MM (used to fold pre-activation usage into the activation month)
                                                const officialMonth = officialActivateDate
                                                    ? (() => { const d = new Date(officialActivateDate); return isNaN(d.getTime()) ? "" : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; })()
                                                    : "";
                                                // aggregate all months up to & including the official activation month into that month
                                                const aggregatedPages: Record<string, number> = {};
                                                Object.entries(lic.monthly_pages || {}).forEach(([m, p]) => {
                                                    if (officialMonth && m <= officialMonth) {
                                                        aggregatedPages[officialMonth] = (aggregatedPages[officialMonth] || 0) + p;
                                                    } else {
                                                        aggregatedPages[m] = (aggregatedPages[m] || 0) + p;
                                                    }
                                                });
                                                const months = Object.entries(aggregatedPages).sort((a, b) => b[0].localeCompare(a[0]));
                                                const fmtMonth = (m: string) => {
                                                    const [y, mo] = m.split("-");
                                                    const d = new Date(Number(y), Number(mo) - 1, 1);
                                                    return isNaN(d.getTime()) ? m : d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
                                                };
                                                const fmtMoney = (v: number) => v.toLocaleString("en-US", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 2 });
                                                return (
                                                    <div className={styles.intervalPricingBody}>
                                                                                                        <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Status</span>
                                                    <span className={`${styles.licenseValue} ${
                                                        remaining.critical
                                                            ? styles.statusCritical
                                                            : remaining.warning
                                                                ? styles.statusWarning
                                                                : remaining.pending ? styles.statusPending : styles.statusActive
                                                    }`}>
                                                        {remaining.critical
                                                            ? "⚠ Expiring Soon"
                                                            : remaining.warning
                                                                ? "● Expiring"
                                                                : remaining.pending ? "● Pending Activation" : "● Active"
                                                        }
                                                    </span>
                                                </div>
                                                        <div className={styles.intervalHistoryTitle}>Interval Pricing</div>
                                                        <div className={styles.intervalParamsGrid}>
                                                            <div className={styles.intervalParamCell}>
                                                                <span className={styles.intervalParamLabel}>Min Monthly</span>
                                                                <span className={styles.intervalParamValue}>{minUsage.toLocaleString()} {lic.measure ?? "pages"}</span>
                                                            </div>
                                                            <div className={styles.intervalParamCell}>
                                                                <span className={styles.intervalParamLabel}>Max Monthly</span>
                                                                <span className={styles.intervalParamValue}>{maxUsage.toLocaleString()} {lic.measure ?? "pages"}</span>
                                                            </div>
                                                            <div className={styles.intervalParamCell}>
                                                                <span className={styles.intervalParamLabel}>Support ≤ Max</span>
                                                                <span className={styles.intervalParamValue}>{fmtMoney(supportUnder)}</span>
                                                            </div>
                                                            <div className={styles.intervalParamCell}>
                                                                <span className={styles.intervalParamLabel}>Support &gt; Max</span>
                                                                <span className={styles.intervalParamValue}>{fmtMoney(supportOver)}</span>
                                                            </div>

                                                        </div>

                                                        <div className={styles.intervalHistoryTitle}>Monthly Usage History</div>
                                                        {months.length === 0 ? (
                                                            <div className={styles.intervalNoHistory}>No usage recorded yet</div>
                                                        ) : (
                                                            <div className={styles.intervalTableWrap}>
                                                                <table className={styles.intervalTable}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th className={styles.intervalThMonth}>Month</th>
                                                                            <th className={styles.intervalThPages}>Pages</th>
                                                                            <th className={styles.intervalThSupport}>Support</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {months.map(([month, pages]) => {
                                                                            const underMin = minUsage > 0 && pages < minUsage;
                                                                            const overMax = maxUsage > 0 && pages > maxUsage;
                                                                            const pagesClass = overMax
                                                                                ? styles.intervalPagesOver
                                                                                : underMin
                                                                                    ? styles.intervalPagesUnder
                                                                                    : styles.intervalPagesNormal;
                                                                            const supportPrice = overMax ? supportOver : supportUnder;
                                                                            return (
                                                                                <tr key={month} className={styles.intervalTr}>
                                                                                    <td className={styles.intervalTdMonth}>{fmtMonth(month)}</td>
                                                                                    <td className={`${styles.intervalTdPages} ${pagesClass}`}>{pages.toLocaleString()}</td>
                                                                                    <td className={`${styles.intervalTdSupport} ${overMax ? styles.intervalSupportOver : ""}`}>{fmtMoney(supportPrice)}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                        {officialActivateDate && (
                                                            <div className={styles.licenseRow}>
                                                                <span className={styles.licenseLabel}>Activated</span>
                                                                <span className={styles.licenseValue}>{formatDate(officialActivateDate)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            <div className={styles.licenseCardBody}>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Status</span>
                                                    <span className={`${styles.licenseValue} ${
                                                        remaining.critical
                                                            ? styles.statusCritical
                                                            : remaining.warning
                                                                ? styles.statusWarning
                                                                : remaining.pending ? styles.statusPending : styles.statusActive
                                                    }`}>
                                                        {remaining.critical
                                                            ? "⚠ Expiring Soon"
                                                            : remaining.warning
                                                                ? "● Expiring"
                                                                : remaining.pending ? "● Pending Activation" : "● Active"
                                                        }
                                                    </span>
                                                </div>

                                                {lic.lic_type == "usage" as lictype ? (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Included Usage</span>
                                                        <span className={styles.licenseValue}>{lic.usage_amount ?? 0} {lic.measure ?? ""}</span>
                                                    </div>
                                                ) : (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Service Level</span>
                                                        <span className={styles.licenseValue}>{lic.service_level ?? ""}</span>
                                                    </div>
                                                )}
                                                {lic.lic_type == "usage" as lictype && (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Total Pages Processed</span>
                                                        <span className={styles.licenseValue}>{lic.total_pages_synced ?? 0}</span>
                                                    </div>
                                                )}
                                                {lic.lic_type == "usage" as lictype && (() => {
                                                    const usage = getUsageStatus(lic.total_pages_synced ?? 0, lic.usage_amount ?? 0);
                                                    return (
                                                        <div className={styles.usageBarContainer}>
                                                            <div className={styles.usageBarTrack}>
                                                                <div
                                                                    className={`${styles.usageBarFill} ${styles[`usageBar${usage.status.charAt(0).toUpperCase() + usage.status.slice(1)}`]}`}
                                                                    style={{ width: `${usage.percent}%` }}
                                                                />
                                                                {usage.status === "over" && (
                                                                    <div className={styles.usageBarOverflow} />
                                                                )}
                                                            </div>
                                                            <div className={styles.usageBarLabels}>
                                                                <span className={`${styles.usageBarText} ${styles[`usageText${usage.status.charAt(0).toUpperCase() + usage.status.slice(1)}`]}`}>
                                                                    {usage.status === "over"
                                                                        ? `⚠ ${((lic.total_pages_synced ?? 0) - (lic.usage_amount ?? 0)).toLocaleString()} over limit`
                                                                        : `${usage.percent.toFixed(1)}% used`
                                                                    }
                                                                </span>
                                                                <span className={styles.usageBarCount}>
                                                                    {(lic.total_pages_synced ?? 0).toLocaleString()} / {(lic.usage_amount ?? 0).toLocaleString()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                                <div className={styles.intervalHistoryTitle}>License Term</div>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Term</span>
                                                    <span className={styles.licenseValue}>{lic.term ?? 0} months</span>
                                                </div>
                                                <div className={styles.licenseRow + " " + (remaining.pending ? styles.remainingRowPending : "")}>
                                                    <span className={styles.licenseLabel}>{remaining.pending ? "Activates In" : "Remaining Time"}</span>
                                                    {remaining.pending ? (
                                                        <span className={`${styles.licenseValue} ${styles.statusPending} ${styles.pendingCountdown}`}>
                                                            ⏳ {remaining.pendingDays} day{remaining.pendingDays !== 1 ? "s" : ""}
                                                        </span>
                                                    ) : (
                                                        <span className={`${styles.licenseValue} ${
                                                            remaining.critical
                                                                ? styles.statusCritical
                                                                : remaining.warning
                                                                    ? styles.statusWarning
                                                                    : ""
                                                        }`}>
                                                            {remaining.critical && "⚠ "}{remaining.days} days
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Term Progress Bar */}
                                                {(() => {
                                                    const termStatus = getTermStatus(lic.activated_on, lic.term);
                                                    return (
                                                        remaining.pending != true && (
                                                            <div className={styles.termBarContainer}>
                                                                <div className={styles.termBarTrack}>
                                                                    <div className={`${styles.termBarFill} ${styles[`termBar${termStatus.status.charAt(0).toUpperCase() + termStatus.status.slice(1)}`]}`} style={{ width: `${termStatus.percentRemaining}%` }}/>
                                                                </div>
                                                                <div className={styles.termBarLabels}>
                                                                    <span className={`${styles.termBarText} ${styles[`termText${termStatus.status.charAt(0).toUpperCase() + termStatus.status.slice(1)}`]}`}>
                                                                        {termStatus.percentRemaining.toFixed(0)}% remaining time
                                                                    </span>
                                                                    <span className={styles.termBarCount}>
                                                                        {termStatus.remainingDays} / {termStatus.totalDays} days
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )
                                                    );
                                                })()}
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>{remaining.pending ? "Activating" : "Activated"}</span>
                                                    <span className={styles.licenseValue}>{formatDate(lic.activated_on)}</span>
                                                </div>
                                                {remaining.pending == true && (
                                                    <CountdownTimer targetDate={new Date(lic.activated_on)} />
                                                )}
                                            </div>
                                        )}
                                    </div>
                            )})}
                        </div>

                        {/* past usage licenses (superseded by a newer usage license) */}
                        {licensesArr.filter(l => isPastUsage(l)).length > 0 && (
                            <>
                                <div className={styles.licensesSectionHeader} style={{ marginTop: "20px" }}>
                                    <span className={styles.licensesSectionTitle}>Past Licenses</span>
                                </div>
                                <div className={styles.licensesGrid}>
                                    {licensesArr.filter(l => isPastUsage(l)).map(lic => {
                                        const remaining = getRemainingTerm(lic.activated_on, lic.term);
                                        const isExpired = remaining.expired;
                                        const cap       = lic.usage_amount ?? 0;
                                        const used      = lic.allocated_pages ?? lic.total_pages_synced ?? 0;
                                        return (
                                        <div key={lic.id} className={`${styles.licenseCard} ${styles.licenseCardInactive}`}>
                                            <div className={`${styles.licenseOpTypeLabel} ${styles.licenseOpTypeLabelInactive}`}>
                                                <div className={styles.opTypeLabelText}>
                                                    {remapLicanceCodeToName[lic.op_type] || ""}
                                                </div>
                                            </div>
                                            <div className={styles.licenseCardHeader}>
                                                <span className={styles.licenseType}>{lic.lic_type || "License"}</span>
                                                <span className={styles.serviceLevelBadge} style={{ backgroundColor: "#94a3b8" }}>
                                                    {isExpired ? "Expired" : "Superseded"}
                                                </span>
                                            </div>
                                            <div className={styles.licenseCardBody}>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Status</span>
                                                    <span className={`${styles.licenseValue} ${styles.statusInactive}`}>
                                                        {isExpired ? "✕ Expired" : "○ Past"}
                                                    </span>
                                                </div>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Included Usage</span>
                                                    <span className={styles.licenseValue}>{cap} {lic.measure ?? ""}</span>
                                                </div>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Pages Consumed</span>
                                                    <span className={styles.licenseValue}>{used.toLocaleString()} / {cap.toLocaleString()}</span>
                                                </div>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Term</span>
                                                    <span className={styles.licenseValue}>{lic.term ?? 0} months</span>
                                                </div>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Activated</span>
                                                    <span className={styles.licenseValue}>{formatDate(lic.activated_on)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )})}
                                </div>
                            </>
                        )}

                        {/* inactive licenses */}
                        {licensesArr.filter(l => (l.is_active != 1 || getRemainingTerm(l.activated_on, l.term).expired) && !isPastUsage(l)).length > 0 && (
                            <>
                                <div className={styles.licensesSectionHeader} style={{ marginTop: "20px" }}>
                                    <span className={styles.licensesSectionTitle}>Inactive Licenses</span>
                                </div>
                                <div className={styles.licensesGrid}>
                                    {licensesArr.filter(l => (l.is_active != 1 || getRemainingTerm(l.activated_on, l.term).expired) && !isPastUsage(l)).map(lic => {
                                        const remaining = getRemainingTerm(lic.activated_on, lic.term);
                                        const isExpired = remaining.expired;
                                        return (
                                        <div key={lic.id} className={`${styles.licenseCard} ${isExpired ? styles.licenseCardExpired : styles.licenseCardInactive}`}>
                                            <div className={`${styles.licenseOpTypeLabel} ${isExpired ? styles.licenseOpTypeLabelExpired : styles.licenseOpTypeLabelInactive}`}>
                                                <div className={styles.opTypeLabelText}>
                                                    {remapLicanceCodeToName[lic.op_type] || ""}
                                                </div>
                                            </div>
                                            <div className={styles.licenseCardHeader}>
                                                <span className={styles.licenseType}>{lic.lic_type || "License"}</span>
                                                <span className={styles.serviceLevelBadge} style={{ backgroundColor: isExpired ? "#dc2626" : "#94a3b8" }}>
                                                    {isExpired ? "Expired" : lic.service_level || "Standard"}
                                                </span>
                                            </div>
                                            <div className={styles.licenseCardBody}>
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Status</span>
                                                    <span className={`${styles.licenseValue} ${isExpired ? styles.statusExpired : styles.statusInactive}`}>
                                                        {isExpired ? "✕ Expired" : "○ Inactive"}
                                                    </span>
                                                </div>
                                                {lic.lic_type == "usage" as lictype ? (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Included Usage</span>
                                                        <span className={styles.licenseValue}>{lic.usage_amount ?? 0} {lic.measure ?? ""}</span>
                                                    </div>
                                                ) : (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Service Level</span>
                                                        <span className={styles.licenseValue}>{lic.service_level ?? ""}</span>
                                                    </div>
                                                )}

                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Term</span>
                                                    <span className={styles.licenseValue}>{lic.term ?? 0} months</span>
                                                </div>
                                                {isExpired && (
                                                    <div className={styles.licenseRow}>
                                                        <span className={styles.licenseLabel}>Expired</span>
                                                        <span className={`${styles.licenseValue} ${styles.statusExpired}`}>
                                                            {Math.abs(remaining.days)} days ago
                                                        </span>
                                                    </div>
                                                )}
                                                <div className={styles.licenseRow}>
                                                    <span className={styles.licenseLabel}>Activated</span>
                                                    <span className={styles.licenseValue}>{formatDate(lic.activated_on)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    )})}
                                </div>
                            </>
                        )}
                    </div>
                    );
                })()}

                <div className={styles.groupFunc}>
                    <TooltipHost content="Restrict the domains that can access the Assistant. Leave empty to allow all domains." directionalHint={DirectionalHint.bottomLeftEdge} calloutProps={tooltipStylesCompAdmin}>
                        <div className={styles.custLabelTagInput}>
                            <span>Allowed Domains</span>
                        </div>
                    </TooltipHost>
                    <TagInput onChange={onAllowedDomainsChange} palceholderText="Add a Domain and press Enter" initialTags={allowedDomains} />
                </div>
                <div className={styles.custLabelTagInput}>
                    <span>Allow Assistant Creation by Quest</span>
                </div>

                <Toggle
                    inlineLabel
                    label={"Quests and Viewers can create Assistants"}
                    styles={toggleStylesCompEdit}
                    checked={editedComp.allowqueststocreate == undefined || editedComp.allowqueststocreate == 1 ? true : false}
                    onChange={(_event, checked) => { handleChange("allowqueststocreate", checked ? 1 : 0)}}
                />

                <div className={styles.modalBottomButs}>
                    <PrimaryButton
                        text="Save"
                        onClick={() => {handleSave()}}
                        disabled={isLoadingAPI}
                        className={styles.saveSpaceButton}
                    />
                </div>
            </Stack>
        </div>
    );
};

export default CompEditForm;
